import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { CompletedInterventionGroupResponse } from '../../api/generated';
import { Checkbox, WarningSign } from '../UI';
import { formatDkk } from '../../utils/currencyUtils';
import styles from './CompletedGroupsWarning.module.css';

interface CompletedGroupsWarningProps {
  // Only the groups the pending removal would strip. Narrowing is the caller's.
  completedGroups: CompletedInterventionGroupResponse[];
  isAcknowledged: boolean;
  onAcknowledgedChange: (isAcknowledged: boolean) => void;
}

/**
 * The count and the money at stake, plus the tick that releases the confirm button. Renders
 * nothing when the given list is empty.
 *
 * The tick itself lives with the caller, keyed to the set of groups it was given for, so
 * widening the scope revokes it. This only reports the change.
 */
export const CompletedGroupsWarning = ({
  completedGroups,
  isAcknowledged,
  onAcknowledgedChange
}: CompletedGroupsWarningProps) => {
  const { t, i18n } = useTranslation();
  const acknowledgementId = useId();

  if (completedGroups.length === 0) return null;

  const total = completedGroups.reduce((sum, group) => sum + (group.amount ?? 0), 0);

  return (
    <div className={styles.warning}>
      <WarningSign variant="error" />
      <div className={styles.body}>
        {/* aria-live so a screen reader hears the figures change when the scope widens,
            rather than only on the first render of the block. */}
        <p className={styles.title} aria-live="polite">
          {t('remove-patient-completed-groups', {
            count: completedGroups.length,
            amount: formatDkk(total, i18n.language)
          })}
        </p>
        <p className={styles.text}>{t('remove-patient-completed-groups-detail')}</p>
        <Checkbox
          id={acknowledgementId}
          isChecked={isAcknowledged}
          onChange={onAcknowledgedChange}
          label={t('remove-plans-history-acknowledge')}
        />
      </div>
    </div>
  );
};
